// Helpers for narrowing `Message.content` (typed `unknown` at the API
// boundary) and for pairing a `tool_use` message with its later `tool_result`.

import type {
  AssistantTextContent,
  ErrorContent,
  Message,
  SystemContent,
  ToolResultContent,
  ToolUseContent,
  UserContent,
} from '../types';

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

// Tool names that spawn a subagent (Anthropic `Task`, future `Agent`
// variants). Single home — previously duplicated in ConversationList,
// MessageBubble and aggregate.ts (sprint ai-elements-rendering hoisted it).
export const SUBAGENT_TOOL_NAMES = new Set(['Task', 'Agent']);

export function asAssistantText(m: Message): AssistantTextContent {
  if (isObj(m.content) && typeof m.content.text === 'string') {
    const out: AssistantTextContent = { text: m.content.text };
    if (typeof m.content.model === 'string') out.model = m.content.model;
    return out;
  }
  return { text: '' };
}

export function asUserContent(m: Message): UserContent {
  if (isObj(m.content) && typeof m.content.text === 'string') {
    return { text: m.content.text };
  }
  return { text: '' };
}

export function asToolUse(m: Message): ToolUseContent {
  if (isObj(m.content)) {
    const id =
      typeof m.content.tool_use_id === 'string' ? m.content.tool_use_id : '';
    const name =
      typeof m.content.name === 'string' ? m.content.name : 'unknown';
    const input = isObj(m.content.input) ? m.content.input : {};
    return { tool_use_id: id, name, input };
  }
  return { tool_use_id: '', name: 'unknown', input: {} };
}

export function asToolResult(m: Message): ToolResultContent {
  if (isObj(m.content)) {
    const id =
      typeof m.content.tool_use_id === 'string' ? m.content.tool_use_id : '';
    return {
      tool_use_id: id,
      content: m.content.content,
      is_error: m.content.is_error === true,
    };
  }
  return { tool_use_id: '', content: null, is_error: false };
}

export function asSystem(m: Message): SystemContent {
  if (isObj(m.content) && typeof m.content.subtype === 'string') {
    return m.content as SystemContent;
  }
  return { subtype: 'unknown' };
}

export function asError(m: Message): ErrorContent {
  if (isObj(m.content)) {
    const out: ErrorContent = {};
    if (typeof m.content.reason === 'string') out.reason = m.content.reason;
    if ('detail' in m.content) out.detail = m.content.detail;
    return out;
  }
  return {};
}

// Stringify a tool_result's content for display. Backend can give us a
// string, an array of blocks, or arbitrary JSON. Anything is safer than
// rendering [object Object].
export function toolResultText(content: unknown): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b) => {
        if (isObj(b) && typeof b.text === 'string') return b.text;
        return JSON.stringify(b, null, 2);
      })
      .join('\n');
  }
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
}

// Build a Map of tool_use_id → matching tool_result message, given the
// agent's full ordered message list. We pair on the explicit id when both
// sides expose one (the common case); otherwise we leave the result
// unpaired and the renderer will show it as a stand-alone block.
export function pairToolResults(messages: Message[]): {
  resultByToolUseId: Map<string, Message>;
  pairedResultIds: Set<string>;
} {
  const resultByToolUseId = new Map<string, Message>();
  const pairedResultIds = new Set<string>();

  for (const m of messages) {
    if (m.type !== 'tool_result') continue;
    const tr = asToolResult(m);
    if (!tr.tool_use_id) continue;
    if (!resultByToolUseId.has(tr.tool_use_id)) {
      resultByToolUseId.set(tr.tool_use_id, m);
      pairedResultIds.add(m.id);
    }
  }

  return { resultByToolUseId, pairedResultIds };
}

// Sprint 5.6 (D9) — group child messages under their parent Agent/Task
// tool_use. Anthropic stamps every event the subagent produces with
// parent_tool_use_id pointing to the parent. Returns:
//   • topLevel: messages where parentToolUseId is null/undefined (rendered
//              in the main timeline).
//   • childrenByParent: parent_tool_use_id → child messages (rendered
//              inside the SubagentPanel; nested subagents recurse on the
//              same map — sprint ai-elements-rendering).
//   • orphanNoticeIds: first message id of each orphan group. An orphan
//              carries a parent_tool_use_id whose tool_use is not in the
//              history (pruned/truncated). Pre-sprint these silently never
//              rendered; now they re-home to topLevel and the first one per
//              missing parent gets a dev-only notice (spec §7).
export function groupBySubagent(messages: Message[]): {
  topLevel: Message[];
  childrenByParent: Map<string, Message[]>;
  orphanNoticeIds: Set<string>;
} {
  // Pass 1 — ids of every tool_use present. During live streaming the
  // parent Task tool_use always lands before its children, so a missing
  // parent means genuinely pruned history, not an ordering race.
  const knownToolUseIds = new Set<string>();
  for (const m of messages) {
    if (m.type !== 'tool_use') continue;
    const id = asToolUse(m).tool_use_id;
    if (id) knownToolUseIds.add(id);
  }

  const topLevel: Message[] = [];
  const childrenByParent = new Map<string, Message[]>();
  const orphanNoticeIds = new Set<string>();
  const noticedParents = new Set<string>();
  for (const m of messages) {
    const parent = m.parentToolUseId ?? null;
    if (!parent) {
      topLevel.push(m);
      continue;
    }
    if (!knownToolUseIds.has(parent)) {
      topLevel.push(m);
      if (!noticedParents.has(parent)) {
        noticedParents.add(parent);
        orphanNoticeIds.add(m.id);
      }
      continue;
    }
    const arr = childrenByParent.get(parent) ?? [];
    arr.push(m);
    childrenByParent.set(parent, arr);
  }
  return { topLevel, childrenByParent, orphanNoticeIds };
}

// Sprint 5.6 (D25) — count distinct models used across assistant turns.
// The per-turn model badge is gated on this set having ≥2 entries (the
// user explicitly switched at least once).
export function distinctModelsInHistory(messages: Message[]): Set<string> {
  const set = new Set<string>();
  for (const m of messages) {
    if (m.type !== 'assistant_text') continue;
    const c = asAssistantText(m);
    if (c.model) set.add(c.model);
  }
  return set;
}

